import { UzButton, UzDialog, UzInput, UzSelect, UzSwitch, UzTextarea } from '@utazzo/ui';
import React, { useEffect, useMemo, useState } from 'react';

import { describeConfig, toConfig, toFormValues, type ConfigFormValues } from '../config-form';
import { SOURCE_LABEL } from '../labels';
import { useCreateLpsConnection, useUpdateLpsConnection } from '../use-lps';

import type { LpsConnection, LpsConnector } from '../lps.api';

import { useQuerySourceOptions } from '@/features/query-sources/use-query-sources';

/**
 * Create or edit a connection. The provider-specific part of the form is
 * generated from the connector's schema; everything around it (name, CRM
 * source, forwarding) is the same for every connector.
 */
export const ConnectionDialog = ({
  open,
  onOpenChange,
  connectors,
  connection,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectors: ReadonlyArray<LpsConnector>;
  /** Present = edit mode. */
  connection: LpsConnection | null;
}): React.JSX.Element => {
  const mode = connection ? 'edit' : 'create';
  const create = useCreateLpsConnection();
  const update = useUpdateLpsConnection();
  const sources = useQuerySourceOptions();

  const [connectorId, setConnectorId] = useState('');
  const [name, setName] = useState('');
  const [crmSourceCode, setCrmSourceCode] = useState('');
  const [forwardToCrm, setForwardToCrm] = useState(true);
  const [values, setValues] = useState<ConfigFormValues>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const connector = connectors.find((c) => c.id === connectorId) ?? null;
  const fields = useMemo(() => (connector ? describeConfig(connector) : []), [connector]);

  // Reset whenever the dialog opens or switches between create and a specific edit.
  useEffect(() => {
    if (!open) return;
    const initialConnector = connection
      ? (connectors.find((c) => c.id === connection.connectorId) ?? null)
      : null;
    setConnectorId(connection?.connectorId ?? '');
    setName(connection?.name ?? '');
    setCrmSourceCode(connection?.crmSourceCode ?? '');
    setForwardToCrm(connection?.forwardToCrm ?? true);
    setValues(
      initialConnector
        ? toFormValues(initialConnector, describeConfig(initialConnector), connection!.config)
        : {},
    );
    setErrors({});
  }, [open, connection, connectors]);

  const pickConnector = (id: string) => {
    setConnectorId(id);
    const next = connectors.find((c) => c.id === id);
    setValues(next ? toFormValues(next, describeConfig(next), null) : {});
    setErrors({});
  };

  const submit = async () => {
    const found: Record<string, string> = {};
    if (!connector) found.connectorId = 'Choose what to connect';
    if (!name.trim()) found.name = 'Give the connection a name';
    const built = toConfig(fields, values, mode);
    Object.assign(found, built.errors);
    setErrors(found);
    if (Object.keys(found).length > 0 || !connector) return;

    const input = {
      name: name.trim(),
      config: built.config,
      crmSourceCode: crmSourceCode || null,
      forwardToCrm,
    };
    try {
      if (connection) await update.mutateAsync({ id: connection.id, input });
      else await create.mutateAsync({ ...input, connectorId: connector.id });
      onOpenChange(false);
    } catch {
      // The hook already showed the server's message; keep the form open to fix it.
    }
  };

  const busy = create.isPending || update.isPending;

  return (
    <UzDialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={mode === 'edit' ? `Edit ${connection?.name}` : 'New connection'}
      description={
        mode === 'edit'
          ? 'Saved secrets are shown masked. Leave them as they are to keep them.'
          : 'After saving you get a webhook URL to paste into the provider.'
      }
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </UzButton>
          <UzButton onClick={() => void submit()} loading={busy}>
            {mode === 'edit' ? 'Save changes' : 'Create connection'}
          </UzButton>
        </div>
      }
    >
      <div className="grid min-w-0 gap-5 px-0.5 pb-1">
        <UzSelect
          label="Connect"
          required
          placeholder="Choose a source"
          value={connectorId}
          onValueChange={pickConnector}
          disabled={mode === 'edit'}
          error={errors.connectorId}
          hint={connector?.description}
          options={connectors.map((c) => ({ value: c.id, label: c.label }))}
        />
        <UzInput
          id="lps-connection-name"
          label="Name"
          required
          placeholder="e.g. Diwali Bali - Meta form"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={errors.name}
        />

        <UzSelect
          label="CRM source"
          hint={
            connector
              ? `Leave on default to file leads under the usual source for ${SOURCE_LABEL[connector.source]}.`
              : undefined
          }
          value={crmSourceCode}
          onValueChange={setCrmSourceCode}
          options={[
            { value: '', label: 'Default for this source' },
            ...(sources.data ?? []).map((s) => ({ value: s.code, label: s.name })),
          ]}
        />

        <div className="rounded-lg border border-zinc-200 px-4 py-3">
          <UzSwitch
            id="lps-forward-to-crm"
            label="Send leads to the CRM"
            description="Off keeps them in the Lead Parsing System only, without assigning anyone."
            checked={forwardToCrm}
            onCheckedChange={setForwardToCrm}
          />
        </div>

        {connector && fields.length > 0 && (
          // min-w-0: a fieldset is min-content wide by default, which pushed the
          // whole form past the dialog edge.
          <fieldset className="grid min-w-0 gap-5 rounded-lg border border-zinc-200 px-4 pt-2 pb-4">
            <legend className="px-1 text-xs font-semibold tracking-wide text-zinc-500 uppercase">
              {connector.label} settings
            </legend>
            {fields.map((f) => {
              const id = `lps-config-${f.key}`;
              const value = values[f.key];
              const set = (v: string | boolean) => setValues((prev) => ({ ...prev, [f.key]: v }));
              if (f.kind === 'boolean') {
                return (
                  <UzSwitch
                    key={f.key}
                    id={id}
                    label={f.label}
                    description={f.hint}
                    checked={value === true}
                    onCheckedChange={set}
                  />
                );
              }
              if (f.kind === 'enum') {
                return (
                  <UzSelect
                    key={f.key}
                    label={f.label}
                    hint={f.hint}
                    required={f.required}
                    value={String(value ?? '')}
                    onValueChange={set}
                    error={errors[f.key]}
                    options={(f.options ?? []).map((o) => ({ value: o, label: o }))}
                  />
                );
              }
              if (f.kind === 'json') {
                return (
                  <UzTextarea
                    key={f.key}
                    id={id}
                    label={f.label}
                    hint={f.hint}
                    rows={4}
                    className="font-mono text-xs"
                    value={String(value ?? '')}
                    onChange={(e) => set(e.target.value)}
                    error={errors[f.key]}
                  />
                );
              }
              return (
                <UzInput
                  key={f.key}
                  id={id}
                  label={f.label}
                  hint={f.hint}
                  required={f.required}
                  type={f.kind === 'secret' ? 'password' : f.kind === 'number' ? 'number' : 'text'}
                  autoComplete={f.kind === 'secret' ? 'new-password' : undefined}
                  value={String(value ?? '')}
                  onChange={(e) => set(e.target.value)}
                  error={errors[f.key]}
                />
              );
            })}
          </fieldset>
        )}
      </div>
    </UzDialog>
  );
};
